import { Card, CardContent } from '@travellegacy/nexops-design-system';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  RotateCcw,
  ShieldCheck,
  Timer,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import type { DemandsOverview } from '../api/demands.api';
import { formatDurationRich } from '../utils/format-duration';

interface Props {
  overview: DemandsOverview | undefined;
  isLoading: boolean;
}

/**
 * Cockpit de KPIs para a página de Demandas.
 *
 * Exibe 6 cards principais:
 *  1. Em andamento agora (activeCount + tempo acumulado)
 *  2. Travadas (stuckCount + threshold)
 *  3. Concluídas no período (closedInPeriod + delta vs. período anterior)
 *  4. Tempo médio de ciclo (averageCycleMs + delta vs. período anterior)
 *  5. Taxa de retrabalho (reworkRate — % de demandas com >1 ciclo no período)
 *  6. Cumprimento de SLA (slaComplianceRate — % de ciclos fechados dentro do SLA do tipo)
 */
export function OverviewKPIs({ overview, isLoading }: Props) {
  if (isLoading || !overview) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Card key={i}>
            <CardContent className="p-5">
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              <div className="mt-3 h-8 w-16 animate-pulse rounded bg-muted" />
              <div className="mt-2 h-3 w-32 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stuckHours = Math.round(overview.stuckThresholdMs / 3_600_000);

  const closedDelta = overview.closedInPeriod - overview.closedInPreviousPeriod;
  const closedDeltaPct =
    overview.closedInPreviousPeriod > 0
      ? (closedDelta / overview.closedInPreviousPeriod) * 100
      : null;

  const avgDelta = overview.averageCycleMs - overview.previousAverageCycleMs;
  const avgDeltaPct =
    overview.previousAverageCycleMs > 0
      ? (avgDelta / overview.previousAverageCycleMs) * 100
      : null;

  // Rework rate: delta em pontos percentuais (não em % de %).
  const reworkDelta = overview.reworkRate - overview.previousReworkRate;
  const reworkDeltaPct =
    overview.previousReworkRate > 0
      ? (reworkDelta / overview.previousReworkRate) * 100
      : null;

  const reworkAlert = overview.reworkRate >= 0.2;

  const slaDelta = overview.slaComplianceRate - overview.previousSlaComplianceRate;
  const slaDeltaPct =
    overview.previousSlaComplianceRate > 0
      ? (slaDelta / overview.previousSlaComplianceRate) * 100
      : null;
  const hasSlaConfigured = overview.slasConfigured > 0;
  const slaBreaching = overview.openCyclesBreachingSLA > 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
      {/* 1. Em andamento agora */}
      <KpiCard
        icon={<Activity className="h-4 w-4 text-emerald-500" />}
        label="Em andamento agora"
        value={overview.activeCount.toString()}
        hint={
          overview.activeCount > 0
            ? `${formatDurationRich(overview.activeElapsedMs)} acumulados`
            : 'Nenhuma demanda ativa'
        }
      />

      {/* 2. Travadas */}
      <KpiCard
        icon={
          <AlertTriangle
            className={`h-4 w-4 ${overview.stuckCount > 0 ? 'text-red-500' : 'text-muted-foreground'}`}
          />
        }
        label="Travadas"
        value={overview.stuckCount.toString()}
        valueClassName={overview.stuckCount > 0 ? 'text-red-500' : undefined}
        hint={`Abertas há mais de ${stuckHours}h`}
      />

      {/* 3. Concluídas no período */}
      <KpiCard
        icon={<CheckCircle2 className="h-4 w-4 text-primary" />}
        label="Concluídas no período"
        value={overview.closedInPeriod.toString()}
        hint={
          <DeltaHint
            delta={closedDelta}
            deltaPct={closedDeltaPct}
            previous={overview.closedInPreviousPeriod}
            format={(n) => n.toString()}
            higherIsBetter
          />
        }
      />

      {/* 4. Tempo médio de ciclo */}
      <KpiCard
        icon={<Timer className="h-4 w-4 text-primary" />}
        label="Tempo médio de ciclo"
        value={overview.averageCycleMs > 0 ? formatDurationRich(overview.averageCycleMs) : '—'}
        hint={
          <DeltaHint
            delta={avgDelta}
            deltaPct={avgDeltaPct}
            previous={overview.previousAverageCycleMs}
            format={(n) => formatDurationRich(Math.abs(n))}
            higherIsBetter={false}
          />
        }
      />

      {/* 5. Taxa de retrabalho */}
      <KpiCard
        icon={
          <RotateCcw
            className={`h-4 w-4 ${reworkAlert ? 'text-amber-500' : 'text-muted-foreground'}`}
          />
        }
        label="Taxa de retrabalho"
        value={
          overview.demandsInPeriod > 0
            ? `${Math.round(overview.reworkRate * 100)}%`
            : '—'
        }
        valueClassName={reworkAlert ? 'text-amber-600 dark:text-amber-400' : undefined}
        hint={
          overview.demandsInPeriod > 0 ? (
            <DeltaHint
              delta={reworkDelta}
              deltaPct={reworkDeltaPct}
              previous={overview.previousReworkRate}
              format={(n) => `${(n * 100).toFixed(0)}pp`}
              higherIsBetter={false}
            />
          ) : (
            'Sem demandas fechadas no período'
          )
        }
      />

      {/* 6. Cumprimento de SLA */}
      <KpiCard
        icon={
          <ShieldCheck
            className={`h-4 w-4 ${
              !hasSlaConfigured
                ? 'text-muted-foreground'
                : slaBreaching
                  ? 'text-red-500'
                  : 'text-emerald-500'
            }`}
          />
        }
        label="Cumprimento de SLA"
        value={
          !hasSlaConfigured || overview.slaCoveredCycles === 0
            ? '—'
            : `${Math.round(overview.slaComplianceRate * 100)}%`
        }
        valueClassName={
          slaBreaching ? 'text-red-600 dark:text-red-400' : undefined
        }
        hint={
          !hasSlaConfigured ? (
            'Configure SLAs por tipo'
          ) : overview.slaCoveredCycles === 0 ? (
            'Sem ciclos com SLA no período'
          ) : slaBreaching ? (
            <span className="text-red-600 dark:text-red-400">
              {overview.openCyclesBreachingSLA} em atraso agora
            </span>
          ) : (
            <DeltaHint
              delta={slaDelta}
              deltaPct={slaDeltaPct}
              previous={overview.previousSlaComplianceRate}
              format={(n) => `${(n * 100).toFixed(0)}pp`}
              higherIsBetter
            />
          )
        }
      />
    </div>
  );
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  valueClassName?: string;
  hint: React.ReactNode;
}

function KpiCard({ icon, label, value, valueClassName, hint }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className={`mt-2 text-3xl font-semibold tabular-nums ${valueClassName ?? ''}`}>
          {value}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      </CardContent>
    </Card>
  );
}

interface DeltaHintProps {
  delta: number;
  deltaPct: number | null;
  previous: number;
  format: (n: number) => string;
  /** Se true, aumento é positivo (verde). Se false (ex: tempo médio), aumento é negativo (vermelho). */
  higherIsBetter: boolean;
}

function DeltaHint({ delta, deltaPct, previous, format, higherIsBetter }: DeltaHintProps) {
  if (previous === 0 && delta === 0) {
    return <span>Sem dados no período anterior</span>;
  }

  const isUp = delta > 0;
  const isFlat = delta === 0;
  const good = isFlat ? true : higherIsBetter ? isUp : !isUp;
  const colorClass = isFlat
    ? 'text-muted-foreground'
    : good
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-red-600 dark:text-red-400';
  const Icon = isFlat ? null : isUp ? TrendingUp : TrendingDown;
  const sign = isUp ? '+' : '';
  const pctLabel =
    deltaPct !== null ? `${sign}${deltaPct.toFixed(0)}%` : `${sign}${format(delta)}`;

  return (
    <span className={`inline-flex items-center gap-1 ${colorClass}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {pctLabel} vs. período anterior
    </span>
  );
}
